import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Stack,
  ButtonGroup,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FormatListNumbered,
  DoneAll,
  Error,
  ContentCopy,
  Update,
  ErrorOutline,
} from "@mui/icons-material";
import { Table as CustomTable } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { getMeeshoOrderHistoryList } from "app/redux/slice/meeshoOrderManagementSlice";
import { usePagination } from "app/hooks/usePagination";
import Loader from "@jumbo/components/Loader";
import moment from "moment";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const OrderHistory = () => {
  const dispatch = useDispatch();

  const meeshoData = useSelector((state) => state?.meeshoOrderManagement);
  const orders = meeshoData?.orderHistory?.list || [];

  // ACTIVE state used for fetch
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
  });
  const [dateRange, setDateRange] = useState([null, null]);

  // TEMP state used for inputs until user clicks Apply
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempFilters, setTempFilters] = useState({
    status: "",
  });
  const [tempDateRange, setTempDateRange] = useState([null, null]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "meesho-management-orders" });

  const handleTempDateChange = (newDateRange) => {
    setTempDateRange(newDateRange);
  };

  const statusOptions = [
    { value: "completed", label: "Completed", color: "success" },
    { value: "failed", label: "Failed", color: "error" },
    { value: "partial", label: "Partial", color: "warning" },
  ];

  // fetch when active params change
  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    rowsPerPage,
    dispatch,
    searchTerm,
    order,
    orderBy,
    filters,
    dateRange,
  ]);

  const fetchOrders = async () => {
    const params = {
      page: page,
      perPage: rowsPerPage,
      ...filters,
    };

    // attach date range only when both are present
    if (dateRange[0] !== null && dateRange[1] !== null) {
      params.fromDate = moment(dateRange[0]).format("YYYY-MM-DD");
      params.toDate = moment(dateRange[1]).format("YYYY-MM-DD");
    }

    dispatch(getMeeshoOrderHistoryList(params));
  };

  const columns = [
    {
      id: "originalFileName",
      label: "File Name",
      sort: false,
      render: (row) => (
        <Tooltip title={row?.originalFileName}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
            {row?.originalFileName}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: "date",
      label: "Date",
      sort: false,
      render: (row) => <>{moment(row?.date).format("DD/MM/YYYY hh:mm A")}</>,
    },
    {
      id: "totalRecords",
      label: "Total",
      sort: false,
    },
    {
      id: "duplicateRecords",
      label: "Duplicate",
      sort: false,
    },
    {
      id: "successfulImports",
      label: "Successful",
      sort: false,
    },
    {
      id: "failedImports",
      label: "Failed",
      sort: false,
    },
    {
      id: "skipRecords",
      label: "Skip",
      sort: false,
    },
    {
      id: "updatedRecords",
      label: "Updated",
      sort: false,
    },
    {
      id: "status",
      label: "Status",
      sort: false,
      render: (row) => <>{getStatusChip(row?.status)}</>,
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => (
        <IconButton
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedOrder(row);
          }}
        >
          <MoreIcon />
        </IconButton>
      ),
    },
  ];

  const sortData = (Order, OrderBy) => {
    setOrder(Order);
    setOrderBy(OrderBy);
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters((prev) => ({ ...prev, [field]: value }));
  };

  // apply temporary inputs to active state + reset page
  const applyFilters = () => {
    setSearchTerm(tempSearchTerm);
    setFilters({ ...tempFilters });
    setDateRange(tempDateRange);
    handleChangePage(0, 0);
  };

  // clear both temp and active filters
  const clearAll = () => {
    setTempSearchTerm("");
    setSearchTerm("");

    const cleared = { status: "" };
    setTempFilters(cleared);
    setFilters(cleared);

    setTempDateRange([null, null]);
    setDateRange([null, null]);

    handleChangePage(0, 0);
  };

  const getStatusChip = (status) => {
    const statusConfig = statusOptions.find(
      (option) => option.value === status
    );
    return (
      <Chip
        label={statusConfig?.label || status}
        color={statusConfig?.color || "default"}
        size="small"
      />
    );
  };

  const statCards = [
    {
      label: "Total",
      value: orderDetails?.totalRecords,
      icon: <FormatListNumbered />,
    },
    {
      label: "Success",
      value: orderDetails?.successfulImports,
      icon: <DoneAll />,
      color: "success",
    },
    {
      label: "Failed",
      value: orderDetails?.failedImports,
      icon: <Error />,
      color: "error",
    },
    {
      label: "Duplicates",
      value: orderDetails?.duplicateRecords,
      icon: <ContentCopy />,
      color: "warning",
    },
    {
      label: "Updated",
      value: orderDetails?.updatedRecords,
      icon: <Update />,
      color: "info",
    },
    {
      label: "Skipped",
      value: orderDetails?.skipRecords,
      icon: <ErrorOutline />,
      color: "secondary",
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Orders History List
      </Typography>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date - To Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={tempDateRange}
                  onChange={handleTempDateChange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ButtonGroup size="small" variant="outlined" fullWidth>
                  {QUICK_RANGES?.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => {
                        // set quick ranges into TEMP so user can inspect before apply
                        setTempDateRange(r.getRange());
                      }}
                      disabled={meeshoData?.orderHistory?.loading}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={tempFilters?.status}
                  onChange={(e) =>
                    handleTempFilterChange("status", e?.target?.value)
                  }
                  label="Status"
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option?.value} value={option?.value}>
                      {option?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={applyFilters}
                fullWidth
                size="small"
                data-testid="apply-filters-btn"
              >
                Apply Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                onClick={clearAll}
                startIcon={<RefreshIcon />}
                fullWidth
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Orders Table */}
      {meeshoData?.orderHistory?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              columns={columns}
              rows={orders}
              page={page}
              rowsPerPage={rowsPerPage}
              count={meeshoData?.orderHistory?.total || 0}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={meeshoData?.orderHistory?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (selectedOrder?._id) {
              setOrderDetails(selectedOrder);
              setDetailsDialog(true);
            }
          }}
        >
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
      </Menu>

      {/* Order History Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Order History Details</DialogTitle>
        <DialogContent dividers>
          {/* File Info */}
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              File: {orderDetails?.originalFileName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Imported By: <strong>{orderDetails?.importedBy?.name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Date: {moment.utc(orderDetails?.date).format("DD/MM/YYYY")}
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards?.map((item, index) => (
              <Grid key={index} item xs={6} sm={4} md={2}>
                <Card
                  sx={{
                    textAlign: "center",
                    borderRadius: 3,
                    background:
                      item.color === "success"
                        ? "#E8F5E9"
                        : item.color === "error"
                        ? "#FFEBEE"
                        : item.color === "warning"
                        ? "#FFF8E1"
                        : item.color === "info"
                        ? "#E3F2FD"
                        : "#F5F5F5",
                  }}
                  elevation={0}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="center" mb={1}>
                      {item.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700}>
                      {item?.value}
                    </Typography>
                    <Typography variant="body2">{item?.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {orderDetails?.issueDetails?.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Error Details:
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                {orderDetails?.issueDetails?.map((error, index) => (
                  <Alert key={index} severity="error" sx={{ mb: 1 }}>
                    Row {error?.row}: {error?.error}
                  </Alert>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderHistory;
